import { useEffect, useMemo } from 'react';

import { ConfigFlags } from 'services/clip.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { setRemovingInstance } from 'components/app/_mediator/app-data.slice';
import { useAppNavigator, useLocation, Path } from 'components/_hooks/use-router';
import { useClipsSwitch } from 'components/app/_hooks/use-clips-switch';
import { EnumDragger, HeldKey, setHeldKey } from 'components/app/_mediator/app.slice';
import { setConfigPanel } from 'components/app/editor/_mediator/editor.slice';
import { setBackupModalListener } from 'components/app/editor/_mediator/editor-controls.slice';
import { useBackup } from 'components/app/editor/_mediator/backup/use-backup-cb';
import { useMarkersCb } from 'components/app/editor/_mediator/editor-markers/use-markers-cb';
import {
  setHotkeyPLayerTimeListener,
  setTimeBoundaryClip,
} from 'components/app/editor/timestamp/_mediator/timestamp.slice';
import { useClipsDelete } from 'components/app/editor/_mediator/editor-clips/use-clips-delete';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { setSbPlayback, setSbWsPlayback } from 'components/app/storyboard/_mediator/sb.slice';
import { setFcPlayback } from 'components/app/finalcuts/_mediator/fc-controls.slice';
import { usePanZoomCb } from 'components/app/editor/pan-zoom/_mediator/use-pan-zoom-cb';
import { isNull, selectClipData } from 'components/app/_helpers/_app.helper';
import { setPlaying } from 'components/app/playground/player/_mediator/pp.slice';
import { useWsSeek } from 'components/app/audio-track/_mediator/use-ws-seek';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { setSrPlayerPlayback } from 'components/app/selector/_mediator/selector.slice';

type SeekDirection = 'backward' | 'forward';
type StepSize = 'Small' | 'Medium';

export const useAppHotkeys = () => {
  const { selectedMarkers, activeClip, clipsMap, configPanel } = useAppSelector((state) => state.editor);
  const { tsLinkedPayload, timeBoundaryClip } = useAppSelector((state) => state.timestamp);
  const { storyModal, heldKey, os, dragger } = useAppSelector((state) => state.app);
  const { playerSeekEnabled } = useAppSelector((state) => state.sb);
  const { removingInstance } = useAppSelector((state) => state.app__data);

  const sbPlayAction = useAppSelector((state) => state.editor__actions.playAction);
  const activeMenuClip = useAppSelector((state) => state.editor.activeMenuClip);
  const selectedFile = useAppSelector(selectSelectedFile);
  const dispatch = useAppDispatch();

  const { path } = useLocation();
  const { navigateTo } = useAppNavigator();

  const backup = useBackup();
  const seekTo = useWsSeek();
  const deleteClip = useClipsDelete();
  const { getPlayerRef } = useEditorCb();
  const { handleNextClip, handlePrevClip } = useClipsSwitch();
  const { delMarkers } = useMarkersCb();
  const { onZoomStep } = usePanZoomCb();
  const { wsGetCursorTime, wsGetDuration } = useAudioTrackContext();

  const clipData = useMemo(() => {
    if (!selectedFile || !activeClip || !clipsMap) return;
    return selectClipData(activeClip, clipsMap);
  }, [activeClip, activeClip && clipsMap?.[activeClip], selectedFile]);

  const seekPlayer = (direction: SeekDirection, stepSize: StepSize, holdShift: boolean) => {
    const player = getPlayerRef();
    if (!player) return;

    const currentTime = player.getCurrentTime();
    const frameRate = selectedFile?.frameRate ? selectedFile.frameRate : 30;
    const smallStep = 1 / frameRate;
    const mediumStep = 15 * smallStep;
    const step = stepSize === 'Small' ? smallStep : mediumStep;

    if (path === Path.storyboard) {
      if (!holdShift && !playerSeekEnabled) {
        const fractionStep = step / wsGetDuration();
        const targetTime = wsGetCursorTime('fraction') + fractionStep * (direction === 'backward' ? -1 : 1);
        seekTo(targetTime);
        return;
      }
      if (!isNull(configPanel)) return;
    }

    const targetTime = direction === 'backward' ? currentTime - step : currentTime + step;
    if ((activeMenuClip || dragger === EnumDragger.tsSlider) && tsLinkedPayload) {
      const { minTime, maxTime, boundaryLeft, boundaryRight } = tsLinkedPayload;
      if (targetTime < minTime || targetTime > maxTime) {
        dispatch(setTimeBoundaryClip(direction === 'backward' ? boundaryLeft : boundaryRight));
        return;
      }
    }
    if (timeBoundaryClip) dispatch(setTimeBoundaryClip(null));
    dispatch(setHotkeyPLayerTimeListener(targetTime));
    player.seekTo(targetTime, 'seconds');
  };

  const isCtrlHeld = (event: KeyboardEvent) => (os === 'mac' && event.metaKey) || (os === 'win' && event.ctrlKey);
  const isKeyIncluded = (event: KeyboardEvent, keys: string[]) => keys.includes(event.key);
  const metaKeys = ['Shift', os === 'mac' ? 'Meta' : 'Control'];
  const removeKeys = ['Delete', 'Backspace'];

  const handleKeydown = (event: KeyboardEvent) => {
    if (storyModal) return;
    const isInputActive = document.activeElement?.tagName?.toLowerCase() === 'input';
    if (isInputActive) return;

    // projects hotkey
    if (path === Path.projects && removeKeys.includes(event.key) && removingInstance)
      dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));

    // editor hotkeys
    if (path !== Path.projects) {
      switch (event.key) {
        case ' ': // playPause
          event.preventDefault();
          if (path === Path.selector) dispatch(setSrPlayerPlayback());
          else if (path === Path.storyboard) {
            if (sbPlayAction === 'audio') dispatch(setSbWsPlayback());
            else dispatch(setSbPlayback());
          } else if (path === Path.finalcuts) dispatch(setFcPlayback());
          else if (path === Path.playerPlayground) dispatch(setPlaying());
          break;
        case removeKeys[0]:
        case removeKeys[1]:
          if (path === Path.finalcuts && removingInstance)
            dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          if (selectedMarkers.length) delMarkers();
          else if (activeClip && !isInputActive) deleteClip(activeClip);
          break;
        case 'Escape':
          dispatch(setConfigPanel(null));
          break;
        case 'j': // prevClip
          event.preventDefault();
          handlePrevClip();
          break;
        case 'k': // nextClip
          event.preventDefault();
          handleNextClip();
          break;
        case 'a':
        case 'A': // seekBackM
          event.preventDefault();
          seekPlayer('backward', 'Medium', event.shiftKey);
          break;
        case 's':
        case 'S': // seekForwardM
          event.preventDefault();
          seekPlayer('forward', 'Medium', event.shiftKey);
          break;
        case 'd':
        case 'D': // seekBackS
          event.preventDefault();
          seekPlayer('backward', 'Small', event.shiftKey);
          break;
        case 'f':
        case 'F': // seekForwardS
          event.preventDefault();
          seekPlayer('forward', 'Small', event.shiftKey);
          break;
        case 'z': // undo/redo
          if (isCtrlHeld(event)) {
            event.preventDefault();
            dispatch(setBackupModalListener(true));
            if (event.shiftKey) backup('redo');
            else backup('undo');
          }
          break;
        case 'Z': // redo for windows
          if (isCtrlHeld(event) && event.shiftKey) {
            event.preventDefault();
            dispatch(setBackupModalListener(true));
            backup('redo');
          }
          break;
        case 'ArrowUp':
          if (clipData?.config.flags.includes(ConfigFlags.panZoomRotate)) onZoomStep(true);
          break;
        case '+':
          if (clipData?.config.flags.includes(ConfigFlags.panZoomRotate)) onZoomStep(true);
          break;
        case '-':
          if (clipData?.config.flags.includes(ConfigFlags.panZoomRotate)) onZoomStep();
          break;
        case 'ArrowDown':
          if (clipData?.config.flags.includes(ConfigFlags.panZoomRotate)) onZoomStep();
          break;
        default:
      }
    }
    // global hotkeys
    switch (event.key) {
      case '1': // Projects
        if (isCtrlHeld(event)) {
          event.preventDefault();
          navigateTo(Path.projects);
        }
        break;
      case '2': // Selector
        if (isCtrlHeld(event)) {
          event.preventDefault();
          navigateTo(Path.selector);
        }
        break;
      case '3': // Storyboard
        if (isCtrlHeld(event)) {
          event.preventDefault();
          navigateTo(Path.storyboard);
        }
        break;
      case '4': // Finalcuts
        if (isCtrlHeld(event)) {
          event.preventDefault();
          navigateTo(Path.finalcuts);
        }
        break;
      default:
        if (isKeyIncluded(event, metaKeys))
          dispatch(setHeldKey((os === 'mac' && event.key === 'Meta' ? 'Control' : event.key) as HeldKey));
        break;
    }
  };

  const handleKeyup = (event: KeyboardEvent) => {
    if (isKeyIncluded(event, metaKeys) && heldKey) {
      if (isCtrlHeld(event) && !event.shiftKey) dispatch(setHeldKey('Control'));
      else if (event.shiftKey) dispatch(setHeldKey('Shift'));
      else dispatch(setHeldKey(null));
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [handleKeydown]);

  useEffect(() => {
    const cb = () => dispatch(setHeldKey(null));
    window.addEventListener('blur', cb);
    return () => window.removeEventListener('blur', cb);
  }, []);
};
